import React, { useState } from "react";
import { Button } from "@mui/material";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { firestore } from "../../firebase";
import { collectPMChecks, fetchPMChecks } from "../../utils/reports/pmData";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import {
  drawReportHeaderLib,
  stampPageNumbersLib,
} from "../../utils/reports/pdfChromeLib";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

// Configuration object for expected PM values
const PM_EXPECTED_VALUES = {
  orig_pets: "No",
  orig_cleanliness: "Yes",
  orig_pest_control: "Yes",
  orig_smoke_detector: "Yes",
  orig_leaks: "Yes",
  orig_ac_filter: "Yes",
  orig_flooring: "Yes",
  orig_appliances: "Yes",
  orig_basic_clean: "Yes",
  orig_bathtub: "Yes",
  orig_fridge_magnets: "Yes",
  orig_sprinkler_head: "Yes",
  orig_sex_trafficking_signage: "Yes",
  orig_toilet: "Yes",
  orig_caulking: "Yes",
  orig_electrical_panel: "Yes",
  orig_windows: "Yes",
  orig_window_locks: "Yes",
  orig_entry_door_lock: "Yes",
  additional_mold_Mildew: "Yes",
};

export default function PMValidationReport({
  clientId,
  selectedProperty,
  rooms,
  currentUserName = "Unknown",
}) {
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const db = firestore;

  const validatePMChecks = (checksWithData) => {
    const failures = {};
    const summary = {};

    // Initialize summary for all PM items
    Object.keys(PM_EXPECTED_VALUES).forEach((key) => {
      summary[key] = { expected: PM_EXPECTED_VALUES[key], failCount: 0 };
      failures[key] = [];
    });

    checksWithData.forEach((check) => {
      if (!check.pmDoc?.responses) return;

      const responses = check.pmDoc.responses;

      Object.entries(PM_EXPECTED_VALUES).forEach(([pmKey, expectedValue]) => {
        const response = responses[pmKey];
        if (!response) return;

        const actualValue = String(response.choice || "").trim();
        const expectedNormalized = expectedValue.toLowerCase();
        const actualNormalized = actualValue.toLowerCase();

        if (actualNormalized !== expectedNormalized) {
          failures[pmKey].push({
            roomName: check.roomName,
            pmDate: check.pmDate,
            expected: expectedValue,
            actual: actualValue,
            notes: response.notes || "",
          });
          summary[pmKey].failCount++;
        }
      });
    });

    return { failures, summary };
  };

  const createValidationPDF = async (validationData, start, end) => {
    const pdf = await PDFDocument.create();
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdf.embedFont(StandardFonts.HelveticaBold);

    // Create cover page with standardized header + summary
    const coverPage = pdf.addPage([612, 792]);
    const { width, height } = coverPage.getSize();
    const margin = 60;

    let y = drawReportHeaderLib(coverPage, {
      title: "PM Validation Report",
      dateRange: { start, end },
      generatedBy: currentUserName,
      font,
      boldFont,
    });
    y -= 10;

    coverPage.drawText("VALIDATION SUMMARY", {
      x: margin,
      y,
      size: 14,
      font: boldFont,
    });
    y -= 20;

    coverPage.drawText("PM Item", { x: margin, y, size: 10, font: boldFont });
    coverPage.drawText("Expected", {
      x: margin + 200,
      y,
      size: 10,
      font: boldFont,
    });
    coverPage.drawText("Failures", {
      x: margin + 300,
      y,
      size: 10,
      font: boldFont,
    });
    y -= 15;

    coverPage.drawLine({
      start: { x: margin, y },
      end: { x: width - margin, y },
      thickness: 1,
      color: rgb(0, 0, 0),
    });
    y -= 10;

    Object.entries(validationData.summary).forEach(([pmKey, data]) => {
      const displayName = pmKey.replace("orig_", "").replace(/_/g, " ");
      const color = data.failCount > 0 ? rgb(0.8, 0, 0) : rgb(0, 0.6, 0);

      coverPage.drawText(displayName, { x: margin, y, size: 9, font });
      coverPage.drawText(data.expected, { x: margin + 200, y, size: 9, font });
      coverPage.drawText(String(data.failCount), {
        x: margin + 300,
        y,
        size: 9,
        font,
        color,
      });
      y -= 12;

      if (y < 100) {
        y = height - 60;
        pdf.addPage([612, 792]);
      }
    });

    // Create detailed pages for each PM item with failures
    Object.entries(validationData.failures).forEach(([pmKey, failureList]) => {
      if (failureList.length === 0) return;

      let detailPage = pdf.addPage([612, 792]);
      let detailY = height - 60;

      const displayName = pmKey
        .replace("orig_", "")
        .replace(/_/g, " ")
        .toUpperCase();
      detailPage.drawText(`${displayName} - FAILURES (${failureList.length})`, {
        x: margin,
        y: detailY,
        size: 16,
        font: boldFont,
        color: rgb(0.8, 0, 0),
      });
      detailY -= 25;

      detailPage.drawText(`Expected: ${PM_EXPECTED_VALUES[pmKey]}`, {
        x: margin,
        y: detailY,
        size: 11,
        font,
      });
      detailY -= 30;

      detailPage.drawText("Room", {
        x: margin,
        y: detailY,
        size: 10,
        font: boldFont,
      });
      detailPage.drawText("Date", {
        x: margin + 80,
        y: detailY,
        size: 10,
        font: boldFont,
      });
      detailPage.drawText("Actual Value", {
        x: margin + 160,
        y: detailY,
        size: 10,
        font: boldFont,
      });
      detailPage.drawText("Notes", {
        x: margin + 260,
        y: detailY,
        size: 10,
        font: boldFont,
      });
      detailY -= 15;

      detailPage.drawLine({
        start: { x: margin, y: detailY },
        end: { x: width - margin, y: detailY },
        thickness: 1,
        color: rgb(0, 0, 0),
      });
      detailY -= 10;

      failureList.forEach((failure) => {
        if (detailY < 100) {
          detailPage = pdf.addPage([612, 792]);
          detailY = height - 60;
        }

        detailPage.drawText(failure.roomName, {
          x: margin,
          y: detailY,
          size: 9,
          font,
        });
        detailPage.drawText(failure.pmDate.toLocaleDateString(), {
          x: margin + 80,
          y: detailY,
          size: 9,
          font,
        });
        detailPage.drawText(failure.actual || "N/A", {
          x: margin + 160,
          y: detailY,
          size: 9,
          font,
        });

        const notes = failure.notes || "";
        if (notes) {
          const maxWidth = 280;
          const words = notes.split(" ");
          let line = "";
          let noteY = detailY;

          words.forEach((word) => {
            const testLine = line + (line ? " " : "") + word;
            if (testLine.length * 5 > maxWidth && line) {
              detailPage.drawText(line, {
                x: margin + 260,
                y: noteY,
                size: 8,
                font,
              });
              line = word;
              noteY -= 10;
            } else {
              line = testLine;
            }
          });
          if (line) {
            detailPage.drawText(line, {
              x: margin + 260,
              y: noteY,
              size: 8,
              font,
            });
          }
        }

        detailY -= 15;
      });
    });

    stampPageNumbersLib(pdf, font);
    return pdf;
  };

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      const pmChecks = collectPMChecks(rooms, start, end);
      if (!pmChecks.length) {
        alert("No PM checks found in the selected date range.");
        return;
      }

      const checksWithData = await fetchPMChecks(db, clientId, selectedProperty, pmChecks);
      const validationData = validatePMChecks(checksWithData);
      const pdf = await createValidationPDF(validationData, start, end);

      const pdfBytes = await pdf.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      downloadBlob(
        blob,
        buildReportFilename("PM_Validation_Report", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (error) {
      console.error("Error generating validation report:", error);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        PM Validation Report
      </Button>

      <ReportDateRangeDialog
        open={open}
        title="Generate PM Validation Report"
        description='Validates PM responses against expected values (most items "Yes", Pets "No") and lists non-conforming rooms.'
        submitLabel="Generate Validation Report"
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
